<template>
    <div class="page course-page lecture-unified-page mx-md-auto">
        <v-container class="pb-0" :class="{'pa-0': phoneOnly}">
            <div v-if="loading"></div>
            <div v-else-if="lecture">
                <div class="lecture-header" :class="{'pt-4': phoneOnly, 'pt-8': pcOnly}">
                    <v-container :class="{'pa-4 pb-0': phoneOnly, 'pb-0': pcOnly}">
                        <div class="d-flex flex-column flex-lg-row align-lg-start">
                            <div
                                class="lecture-header__image"
                                :class="{'mb-4': phoneOnly, 'me-lg-8': pcOnly}"
                            >
                                <v-img
                                    v-if="lecture.image_url"
                                    :src="lecture.image_url"
                                    :aspect-ratio="phoneOnly ? 16/9 : undefined"
                                    :height="pcOnly ? 275 : undefined"
                                    :width="pcOnly ? 400 : undefined"
                                    class="lecture-header__img"
                                    cover
                                />
                                <v-sheet
                                    v-else
                                    outlined
                                    rounded
                                    class="d-flex align-center justify-center lecture-header__placeholder"
                                >
                                    <v-icon color="grey lighten-1" :size="pcOnly ? 64 : 48">mdi-play-box-outline</v-icon>
                                </v-sheet>
                            </div>

                            <div
                                class="lecture-header__body flex-grow-1 min-width-0"
                                :class="phoneOnly ? 'text-center px-1' : 'text-start'"
                            >
                                <h1 v-if="pcOnly" class="font-weight-medium mb-3 mb-lg-2 lecture-header__title">{{ lecture.name }}</h1>
                                <h3 v-else class="font-weight-medium mb-2 lecture-header__title">{{ lecture.name }}</h3>

                                <div class="lecture-meta d-flex flex-wrap" :class="{'justify-center': phoneOnly, 'justify-start': !phoneOnly, 'mb-3 mb-lg-4': !!lecture.description}">
                                    <v-chip
                                        v-if="lecture.course"
                                        outlined
                                        label
                                        :small="phoneOnly"
                                        :to="{ name: 'course', params: { id: lecture.course.id } }"
                                        v-ripple="phoneOnly"
                                        class="me-1 me-lg-2 my-1 px-2"
                                    >
                                        <v-icon :size="phoneOnly ? 16 : 18" class="me-2">mdi-book-open-variant</v-icon>
                                        {{ lecture.course.name }}
                                    </v-chip>
                                    <v-chip
                                        v-if="lecture.teacher"
                                        outlined
                                        label
                                        :to="{ name: 'teacher', params: { id: lecture.teacher.id } }"
                                        v-ripple="phoneOnly"
                                        :small="phoneOnly"
                                        class="me-1 me-lg-2 my-1 ps-1 pe-2"
                                    >
                                        <v-avatar v-if="lecture.teacher.avatar" tile class="me-2 rounded" :style="phoneOnly ? 'height: 18px!important; min-width: 18px!important; width: 18px!important;' : ''">
                                            <img :src="lecture.teacher.avatar" alt="">
                                        </v-avatar>
                                        {{ lecture.teacher.name }}
                                    </v-chip>
                                    <v-chip v-if="lecture.preview && payByCourse" :small="phoneOnly" label outlined color="deep-purple accent-4" class="me-1 me-lg-2 my-1 px-2">
                                        <v-icon :size="phoneOnly ? 16 : 18" class="me-2">mdi-play-speed</v-icon>
                                        معاينة
                                    </v-chip>
                                    <v-chip v-if="lecture.seen" :small="phoneOnly" label outlined color="success darken-1" class="me-1 me-lg-2 my-1 px-2">
                                        <v-icon :size="phoneOnly ? 16 : 18" class="me-2">mdi-check-circle-outline</v-icon>
                                        مكتملة
                                    </v-chip>
                                    <v-chip v-if="lecture.expired" :small="phoneOnly" label outlined color="error" class="me-1 me-lg-2 my-1 px-2">
                                        <v-icon :size="phoneOnly ? 16 : 18" class="me-2">mdi-clock-alert-outline</v-icon>
                                        منتهية الصلاحية
                                    </v-chip>
                                    <v-chip v-else-if="lecture.expiry_days" :small="phoneOnly" label outlined color="orange darken-2" class="me-1 me-lg-2 my-1 px-2">
                                        <v-icon :size="phoneOnly ? 16 : 18" class="me-2">mdi-clock-outline</v-icon>
                                        {{ unitFormat(lecture.expiry_days, 'يوم', 'يومين', 'أيام') }} متبقية
                                    </v-chip>
                                    <v-chip v-if="payByLecture && !lecture.purchased && !lecture.free && lecture.price" :small="phoneOnly" label outlined color="primary" class="my-1 px-2">
                                        <v-icon :size="phoneOnly ? 16 : 18" class="me-2">mdi-cash</v-icon>
                                        {{ moneyFormat(lecture.price, true) }}
                                    </v-chip>
                                </div>

                                <div v-if="lecture.description" class="description-wrapper mb-3 mb-lg-4">
                                    <p style="line-height: 2.2; white-space: pre-line;" class="medium-text text--text mb-0 text-start">
                                        <span v-if="lecture.description.length < maxDescriptionLength" v-html="urlify(escapeHtmlText(lecture.description))"></span>
                                        <span v-else>
                                            <span v-html="urlify(escapeHtmlText(showMoreDescription ? lecture.description : lecture.description.slice(0, maxDescriptionLength)))"></span><span v-if="!showMoreDescription">...</span>
                                            <a v-if="!showMoreDescription" text class="link--text ms-1" @click="showMoreDescription = true" style="white-space: nowrap">عرض المزيد</a>
                                            <a v-else text class="link--text ms-2" @click="showMoreDescription = false" style="white-space: nowrap">عرض أقل</a>
                                        </span>
                                    </p>
                                </div>

                                <div v-if="hasContentStats" class="lecture-stats">
                                    <div class="d-flex flex-wrap lecture-stats__chips mb-3 mb-lg-5" :class="phoneOnly ? 'justify-center' : 'justify-start'">
                                        <v-chip v-if="contentStats.videos" outlined :small="phoneOnly" label class="me-1 me-lg-2 my-1 px-2">
                                            <v-icon :size="phoneOnly ? 16 : 18" class="me-2" color="primary">mdi-play-box-multiple-outline</v-icon>
                                            {{ unitFormat(contentStats.videos, 'محاضرة', 'محاضرتين', 'محاضرات') }}
                                        </v-chip>
                                        <v-chip v-if="contentStats.exams" outlined :small="phoneOnly" label class="me-1 me-lg-2 my-1 px-2">
                                            <v-icon :size="phoneOnly ? 16 : 18" class="me-2" color="teal darken-1">mdi-clipboard-text-outline</v-icon>
                                            {{ unitFormat(contentStats.exams, 'امتحان', 'امتحانين', 'امتحانات') }}
                                        </v-chip>
                                        <v-chip v-if="contentStats.attachments" outlined :small="phoneOnly" label class="me-1 me-lg-2 my-1 px-2">
                                            <v-icon :size="phoneOnly ? 16 : 18" class="me-2" color="strong-text">mdi-paperclip</v-icon>
                                            {{ unitFormat(contentStats.attachments, 'مرفق', 'مرفقين', 'مرفقات') }}
                                        </v-chip>
                                    </div>

                                    <div v-if="contentStats.total > 0" class="lecture-stats__progress mt-3" :class="phoneOnly ? 'mx-1' : ''">
                                        <div class="d-flex align-center justify-space-between mb-1 medium-text muted--text">
                                            <span>التقدم في المحاضرة</span>
                                            <span>{{ contentStats.completed }} / {{ contentStats.total }}</span>
                                        </div>
                                        <v-progress-linear
                                            :value="contentStats.progressPercent"
                                            height="8"
                                            rounded
                                            :color="lecture.seen ? 'success' : 'primary'"
                                        ></v-progress-linear>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-container>
                </div>

                <v-divider v-if="phoneOnly" class="mt-6"></v-divider>

                <div class="mt-lg-8">
                    <v-container class="py-lg-6 py-0" :class="{'px-0': phoneOnly}">
                        <v-card class="py-2 px-lg-5 lg:bordered" :flat="phoneOnly">
                            <v-card-title v-if="pcOnly" class="px-0 pb-2">
                                <v-icon color="strong-text" class="me-2">mdi-view-list-outline</v-icon>
                                محتويات المحاضرة
                            </v-card-title>
                            <v-card-text :class="{'pa-0': phoneOnly, 'px-0': pcOnly}">
                                <v-alert v-if="platform === 'web' && !miniDerasaPlatform && !wpEnabled" text dark color="primary" class="blink-animation" :class="phoneOnly ? 'ma-3 pa-4' : 'mb-3 px-0 py-3'">
                                    <div class="d-flex align-center" :class="{'flex-column justify-center': phoneOnly, 'justify-space-between': pcOnly}">
                                        <div :class="{'text-center': phoneOnly, 'text-start': pcOnly}">
                                            قم بتحميل {{ phoneOnly ? 'تطبيق الهاتف' : 'برنامج الديسكتوب' }} حتى تتمكن من مشاهدة المحاضرات
                                        </div>

                                        <div :class="{'ms-4': pcOnly, 'mt-2': phoneOnly}">
                                            <v-btn v-if="pcOnly" depressed :block="phoneOnly" color="primary" tag="a" download :href="appData.desktop_app_url">
                                                <v-icon size="20" class="ml-2" color="white">mdi-monitor</v-icon>
                                                تحميل البرنامج
                                            </v-btn>

                                            <div v-else class="store-badges d-flex align-center justify-center pa-0">
                                                <store-badge type="playstore" width="135px" />
                                                <store-badge type="appstore" width="106px" />
                                            </div>
                                        </div>
                                    </div>
                                </v-alert>

                                <lecture-contents-list
                                    v-if="items && items.length"
                                    :items="items"
                                    :attachments="attachments"
                                    :lecture="lecture"
                                    :opening-exam="openingExam"
                                    attendance-chip-placement="subtitle"
                                    @play-video="playVideo"
                                    @open-exam="openExam"
                                />
                                <div v-else-if="attachments && attachments.length">
                                    <lecture-contents-list
                                        :items="[]"
                                        :attachments="attachments"
                                        :lecture="lecture"
                                        :opening-exam="openingExam"
                                        attendance-chip-placement="subtitle"
                                        @play-video="playVideo"
                                        @open-exam="openExam"
                                    />
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-container>
                </div>
            </div>

            <v-dialog v-model="phoneDownloadDialog" max-width="400" overlay-color="#000" overlay-opacity="0.8">
                <v-card>
                    <v-card-title class="mb-2">حمل تطبيق الهاتف</v-card-title>
                    <v-card-subtitle>لكي تتمكن من مشاهدة المحاضرة؛ يجب عليك تحميل تطبيق الهاتف</v-card-subtitle>
                    <v-card-text>
                        <div class="d-flex justify-center align-center">
                            <store-badge type="playstore" width="150px" />
                            <store-badge type="appstore" width="120px" />
                        </div>
                    </v-card-text>
                </v-card>
            </v-dialog>
            <video-dialog v-model="showVideoDialog" :video="showVideo" />
        </v-container>
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

import VideoDialog from '@/components/custom/video-dialog'
import LectureContentsList from '@/components/lecture/unified/lecture-contents-list'
import StoreBadge from '@/components/default/store-badge'

import { PrivacyScreen } from '@capacitor-community/privacy-screen';

import * as linkify from 'linkifyjs';
import linkifyHtml from 'linkify-html';

export default {
    name: "LectureUnified",
    components: {
        StoreBadge,
        VideoDialog,
        LectureContentsList,
    },
    data(){
        return {
            lecture: null,
            loading: false,
            items: null,
            attachments: null,
            showMoreDescription: false,
            phoneDownloadDialog: false,
            showVideoDialog: false,
            showVideo: null,
            openingExam: false,
        }
    },
    computed: {
        ...GlobalComputed,
        id(){
            return this.$route.params.id;
        },
        maxDescriptionLength(){
            return this.pcOnly ? 350 : 200;
        },
        payByCourse(){
            return this.lecture && this.lecture.course && this.lecture.course.pay_type === 'course';
        },
        payByLecture(){
            return this.lecture && this.lecture.course && this.lecture.course.pay_type === 'lectures';
        },
        contentStats(){
            const lecture = this.lecture || {};
            const items = this.items || [];
            let completed = 0;

            items.forEach((entry) => {
                if (entry.type === 'video' && entry.item && entry.item.seen){
                    completed += 1;
                }else if (entry.type === 'exam' && entry.item && !entry.item.needs_attention){
                    completed += 1;
                }
            });

            const videos = lecture.videos_count || items.filter(i => i.type === 'video').length;
            const exams = lecture.exams_count || items.filter(i => i.type === 'exam').length;
            const attachments = lecture.attachments_count || (this.attachments ? this.attachments.length : 0);
            const total = items.length;
            const progressPercent = total ? Math.round((completed / total) * 100) : 0;

            return { videos, exams, attachments, total, completed, progressPercent };
        },
        hasContentStats(){
            const s = this.contentStats;
            return s.videos || s.exams || s.attachments || s.total;
        },
    },
    methods: {
        ...GlobalMethods,

        playVideo(video){
            let wpEnabled = this.wpEnabled;

            if (this.platform === 'web' && !wpEnabled && !this.miniDerasaPlatform){
                if (this.pcOnly){
                    const apiToken = window.localStorage.getItem('tK');
                    const deepLinkProtocol = process.env.VUE_APP_DESKTOP_APP_PROTOCOL;
                    const url = `${deepLinkProtocol}://video?videoId=${video.id}&token=${apiToken}`
                    window.open(url, '_self')
                }else{
                    this.phoneDownloadDialog = true;
                }
            }else if (this.platform === 'android' || this.platform === 'ios' || this.miniDerasaPlatform || wpEnabled){
                window.setTimeout(()=>{
                    this.showVideoDialog = true;
                    this.showVideo = video;
                }, 200);
            }
        },

        openExam(exam){
            const exam_code = exam.navigate_to_alternative && exam.alternative_exam ? exam.alternative_exam.exam_code : exam.exam_code;
            if (!this.openingExam){
                this.openingExam = true;
                window.axios.get(`/exams/details?exam_code=${exam_code}`).then(response=>{
                    this.openingExam = false;
                    const examData = response.data.data;
                    window.setTimeout(()=>{
                        this.$router.push({name: 'exams', params: {examData: examData}, query: {course_id: this.lecture.course_id}})
                    }, 600);

                }).catch(error=>{
                    if (error && error.response && error.response.data){
                        const msg = error.response.data;
                        if (msg === 'EXAM_NOT_FOUND'){
                            this.showMsg('الامتحان غير موجود', 'error');
                        }else if (msg === 'EXAM_NOT_STARTED'){
                            this.showMsg('لم يبدأ الامتحان بعد، انتظر حتى يأتي وقت الامتحان', 'error');
                        }else if (msg === 'EXAM_ENDED'){
                            this.showMsg('انتهى الوقت المسموح فيه بدخول الامتحان', 'error');
                        }else{
                            this.showMsg('حدثت مشكلة أثناء جلب بيانات الامتحان', 'error');
                        }
                    }else{
                        this.showMsg('حدثت مشكلة أثناء جلب بيانات الامتحان', 'error');
                    }
                    this.openingExam = false;
                })
            }
        },

        loadData(){
            if (!this.loading){
                this.loading = true;
                window.axios.get(`/lectures/${this.id}/contents/unified`).then(response=>{
                    this.setDRM();

                    this.loading = false;
                    this.lecture = response.data.lecture;
                    this.items = response.data.items;
                    this.attachments = response.data.attachments;

                    this.setDocumentTitle(this.lecture.name, true);
                }).catch(e=>{
                    this.loading = false;
                    if (e.response && e.response.data && e.response.data.course_id){
                        this.$router.push({name: 'course', params: {id: e.response.data.course_id}});
                        this.showMsg('يتعين عليك شراء المحاضرة أولاً', 'error');
                    }else{
                        this.showMsg('حدثت مشكلة أثناء تحميل البيانات', 'error');
                    }
                })
            }
        },

        setDRM(){
            if (this.platform === 'android'){
                return PrivacyScreen.enable();
            }
        },
        removeDRM(){
            if (this.platform === 'android'){
                return PrivacyScreen.disable();
            }
        },
        urlify(text){
            const options = { defaultProtocol: 'https' , target: '_system'};
            return linkifyHtml(text, options);
        },
        escapeHtmlText(text){
            return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
        },
        backButtonHandler(){
            if (this.showVideoDialog){
                this.showVideoDialog = false;
            }else{
                this.$router.push({name: 'course', params: {id: this.lecture.course_id}});
            }
        },
    },
    watch: {
        loading(loading){
            this.setLinearLoading(loading);
        },
        showVideoDialog(show){
            if (!show){
                this.loadData();
            }
        },
    },
    mounted(){
        if (this.appData && this.appData.lectures_view_layout !== 'seperated'){
            this.$router.push('/not-found');
        }
        this.loadData();

        if (this.capRunning){
            document.addEventListener('backbuttonclicked', this.backButtonHandler);
        }
    },
    beforeDestroy(){
        this.removeDRM();
        if (this.capRunning){
            document.removeEventListener('backbuttonclicked', this.backButtonHandler);
        }
    }
};
</script>

<style lang="scss" scoped>
.lecture-unified-page {
    .lecture-header__title {
        line-height: 1.45;
        word-break: break-word;
    }

    .lecture-header__image {
        width: 100%;
        max-width: 100%;
        min-width: 0;
    }

    .lecture-header__img,
    .lecture-header__placeholder {
        width: 100%;
        max-width: 100%;
        border-radius: 10px;
        overflow: hidden;
    }

    .lecture-header__placeholder {
        aspect-ratio: 16 / 9;
    }

    .lecture-header__body {
        width: 100%;
    }

    .description-wrapper {
        text-align: start;
    }

    .lecture-stats__progress {
        max-width: 420px;
    }

    .min-width-0 {
        min-width: 0;
    }
}

@media (max-width: 960px) {
    .lecture-unified-page {
        .lecture-header__title {
            font-size: 1.25rem;
        }

        .lecture-stats__progress {
            max-width: 100%;
        }
    }
}

@media (min-width: 960px) {
    .lecture-unified-page {
        .lecture-header {
            .container {
                min-height: 240px;
            }
        }

        .lecture-header__image {
            width: 400px;
            max-width: 400px;
            flex-shrink: 0;
        }

        .lecture-header__img,
        .lecture-header__placeholder {
            border-radius: 15px;
        }

        .lecture-header__placeholder {
            height: 275px;
            aspect-ratio: auto;
        }

        .lecture-header__body {
            padding-top: 8px;
        }
    }
}
</style>
